import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import IconTiStar from '@components/Images/IconTiStar';
import IconArrow from '@components/Images/Rebranding/IconArrow';
import useEventTracking from '@hooks/useEventTracking';
import { ParsedTakeaway } from '@interfaces/homepage';
import { deep_research_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import styles from './styles.module.scss';

interface TakeawayProps {
  takeaway: ParsedTakeaway[];
}

const DEEP_RESEARCH = 'Deep Research';

const renderTakeawayContent = (takeaway: ParsedTakeaway[]) => {
  return takeaway.map((block, i) => {
    if (block.type === 'list') {
      return (
        <ul key={i} className="!mb-16 p-0">
          {block.items.map((item, index) => (
            <li key={index} className="flex items-start gap-10 text-white">
              <IconArrow className="mt-[9px] h-[14px] w-[14px] flex-shrink-0 rotate-180 text-white" />
              <div>{AuthoredContentReactToHtmlParser(item)}</div>
            </li>
          ))}
        </ul>
      );
    }

    return AuthoredContentReactToHtmlParser(block.content || '');
  });
};

const Takeaway = ({ takeaway }: TakeawayProps) => {
  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.Takeaway,
  });

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: ImpressionElement.Takeaway }));
        }}
      >
        <div
          className={clsx(
            styles.takeawayContainer,
            '-mx-16 my-32 bg-grayscale-charcoal p-20 sm:-mx-28 sm:pb-40 sm:pl-32 sm:pr-64 sm:pt-32 md:float-left md:my-0 md:-ml-[88px] md:mr-20 md:w-[348px] md:pr-40 lg:-ml-[320px] lg:w-[638px] lg:pb-40 lg:pr-[45px] lg:pt-32 xl:mr-40',
            'dark:bg-[var(--light-grayscale-charcoal)]'
          )}
        >
          <div className="font-suisse-neue text-body-lg text-white *:!text-white md:ml-auto md:max-w-[356px] print:text-black">
            <h3 className="m-0 mb-16 border-0 border-b border-solid border-grayscale-gray pb-16 !font-suisse-intl-mono text-body-takeaway text-white print:text-black">
              The Takeaway
            </h3>
            <div>{renderTakeawayContent(takeaway)}</div>
            <div className="mt-16 border-0 border-t border-solid border-grayscale-gray pt-16">
              <div className="flex items-center gap-8">
                <IconTiStar className="-mt-4 flex-shrink-0" />
                <p className="!m-0 font-suisse-neue text-body-lg text-white">
                  <span>Powered by </span>
                  <a
                    href={deep_research_path()}
                    onClickCapture={(event) => {
                      event.stopPropagation();
                      trackEvent(
                        click({
                          clickTarget: ClickTarget.DeepResearch,
                          clickText: DEEP_RESEARCH,
                        })
                      );
                    }}
                    className="underline hover:text-primary-magenta dark:hover:text-primary-pink"
                  >
                    {DEEP_RESEARCH}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </InView>
    </Track>
  );
};

export default Takeaway;
